// Penyimpanan sesi auth di sessionStorage (rfc-005 §3: token tidak di
// localStorage panjang umur). Hanya token + identitas akun + permission
// efektif yang disimpan — TIDAK ada modal/cost/laba (NFR-SEC-3).

import type { LoginResponse } from "../api";

export type Session = {
  accessToken: string;
  refreshToken: string;
  deviceId: string;
  // Cabang aktif sesi (RFC-007). branchId menentukan stok/harga/shift yang
  // di-cache & ditampilkan; branchViewAll = boleh lintas cabang (Pemilik).
  // branchCode = prefix nomor struk (mis. "TGR-01"). TIDAK ada modal/cost.
  branchId: string;
  branchViewAll: boolean;
  branchCode?: string;
  account: LoginResponse["account"];
};

const KEY = "pos.session.v1";

export function loadSession(): Session | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    if (!parsed.accessToken || !parsed.account?.id) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveSession(session: Session): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(session));
  } catch {
    // quota / private mode — abaikan; sesi tetap hidup di memori.
  }
}

export function clearSession(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // abaikan
  }
}

// Kunci layar idle bergaya "JWT sliding-expiry": simpan WAKTU KEDALUWARSA
// (timestamp) bukti aktivitas terakhir + accountId, di sessionStorage yang SAMA
// dgn sesi. Aktivitas memperpanjang (refresh) expiry; idle melewati expiry =
// terkunci; PIN benar memperbarui expiry. FAIL-CLOSED: marker hilang/lewat/akun
// beda = terkunci. Karena timestamp persist, RELOAD tak mereset jam idle, dan
// hapus sessionStorage = ikut logout.
export const IDLE_MS = 5 * 60 * 1000;
const LOCK_KEY = "pos.unlock.v1";

type Unlock = { acc: string; exp: number };

// touchScreenUnlock: perpanjang masa "tak terkunci" (now + IDLE_MS) untuk akun.
export function touchScreenUnlock(accountId: string): void {
  try {
    sessionStorage.setItem(LOCK_KEY, JSON.stringify({ acc: accountId, exp: Date.now() + IDLE_MS }));
  } catch {
    // abaikan
  }
}

export function clearScreenUnlock(): void {
  try {
    sessionStorage.removeItem(LOCK_KEY);
  } catch {
    // abaikan
  }
}

export function screenUnlock(): Unlock | null {
  try {
    const raw = sessionStorage.getItem(LOCK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Unlock;
    if (typeof parsed.exp !== "number" || typeof parsed.acc !== "string") return null;
    return parsed;
  } catch {
    return null;
  }
}

// screenLocked: true bila harus terkunci untuk akun ini (fail-closed).
export function screenLocked(accountId: string): boolean {
  const u = screenUnlock();
  return !u || u.acc !== accountId || Date.now() >= u.exp;
}
